import React from 'react';
import { 
  FiDollarSign, 
  FiTrendingUp, 
  FiTarget, 
  FiCheckCircle,
  FiArrowRight,
  FiZap,
  FiBarChart,
  FiMousePointer
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { Image } from '../../components/ui/Image';
import { imageConfig } from '../../config/images';

export const PPCPage: React.FC = () => {
  const ppcServices = [
    {
      title: 'Google Ads Management',
      description: 'Maximize ROI with expertly managed Google Ads campaigns',
      icon: <FiTarget className="text-4xl" />,
      features: [
        'Search Ads Campaign Setup',
        'Display Network Advertising',
        'Shopping Ads for E-commerce',
        'YouTube Video Ads',
        'Remarketing Campaigns',
        'Performance Max Campaigns'
      ],
      price: '₹20,000',
      duration: '/month + ad spend',
      roi: 'Average 320% ROAS'
    },
    {
      title: 'Facebook & Instagram Ads',
      description: 'Reach your ideal audience on Meta platforms',
      icon: <FiMousePointer className="text-4xl" />,
      features: [
        'Audience Research & Targeting',
        'Creative Design & Copywriting',
        'A/B Testing & Optimization',
        'Instagram Stories & Reels Ads',
        'Lead Generation Campaigns',
        'Conversion Tracking Setup'
      ],
      price: '₹18,000',
      duration: '/month + ad spend',
      roi: 'Average 280% ROAS'
    },
    {
      title: 'LinkedIn Ads',
      description: 'B2B focused advertising for professional audiences',
      icon: <FiBarChart className="text-4xl" />,
      features: [
        'Sponsored Content',
        'InMail Campaigns',
        'Text & Dynamic Ads',
        'Account-Based Marketing',
        'Lead Gen Forms',
        'Conversion Tracking'
      ],
      price: '₹25,000',
      duration: '/month + ad spend',
      roi: 'Average 250% ROAS'
    },
    {
      title: 'Microsoft Ads',
      description: 'Tap into Bing and Microsoft Audience Network',
      icon: <FiZap className="text-4xl" />,
      features: [
        'Bing Search Ads',
        'Microsoft Audience Network',
        'Shopping Campaigns',
        'Import from Google Ads',
        'Lower CPC Opportunities',
        'Performance Reporting'
      ],
      price: '₹15,000',
      duration: '/month + ad spend',
      roi: 'Average 290% ROAS'
    },
    {
      title: 'Amazon Advertising',
      description: 'Boost product visibility and sales on Amazon',
      icon: <FiTrendingUp className="text-4xl" />,
      features: [
        'Sponsored Products',
        'Sponsored Brands',
        'Sponsored Display',
        'Amazon DSP',
        'Product Targeting',
        'Amazon Attribution'
      ],
      price: '₹22,000',
      duration: '/month + ad spend',
      roi: 'Average 340% ROAS'
    },
    {
      title: 'Remarketing Campaigns',
      description: 'Re-engage visitors and boost conversions',
      icon: <FiMousePointer className="text-4xl" />,
      features: [
        'Display Remarketing',
        'RLSA (Search Remarketing)',
        'Dynamic Remarketing',
        'Social Media Retargeting',
        'Email Retargeting',
        'Cross-Platform Strategies'
      ],
      price: '₹16,000',
      duration: '/month + ad spend',
      roi: 'Average 380% ROAS'
    }
  ];

  const ppcPackages = [
    {
      name: 'Starter PPC',
      price: '₹15,000',
      period: '/month',
      adSpend: '+ ad spend',
      description: 'Perfect for businesses starting with paid ads',
      features: [
        '1 platform (Google or Facebook)',
        'Up to 2 campaigns',
        'Basic targeting',
        'Monthly reporting',
        'Email support'
      ],
      popular: false
    },
    {
      name: 'Professional PPC',
      price: '₹35,000',
      period: '/month',
      adSpend: '+ ad spend',
      description: 'Comprehensive PPC management for growth',
      features: [
        '2-3 platforms',
        'Up to 6 campaigns',
        'Advanced targeting',
        'A/B testing',
        'Bi-weekly reporting',
        'Dedicated manager'
      ],
      popular: true
    },
    {
      name: 'Enterprise PPC',
      price: 'Custom',
      period: 'pricing',
      adSpend: '',
      description: 'Advanced PPC for large-scale campaigns',
      features: [
        'All platforms',
        'Unlimited campaigns',
        'Advanced strategies',
        'Real-time optimization',
        'Weekly reporting',
        'Dedicated team',
        'Priority support'
      ],
      popular: false
    }
  ];

  const results = [
    {
      metric: '320%',
      label: 'Average ROAS',
      icon: <FiDollarSign className="text-3xl" />
    },
    {
      metric: '+285%',
      label: 'Conversion Rate Increase',
      icon: <FiTrendingUp className="text-3xl" />
    },
    {
      metric: '-45%',
      label: 'Cost Per Acquisition',
      icon: <FiTarget className="text-3xl" />
    },
    {
      metric: '98%',
      label: 'Client Satisfaction',
      icon: <FiCheckCircle className="text-3xl" />
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-32 bg-gradient-to-br from-orange-600 via-red-600 to-pink-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-6xl font-bold mb-8">
            PPC <span className="text-yellow-400">Advertising</span>
          </h1>
          <p className="text-2xl mb-12 max-w-4xl mx-auto opacity-90">
            Drive immediate results and qualified traffic with strategic paid advertising 
            campaigns that maximize your ROI and grow your business.
          </p>
          
          <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {results.map((result, index) => (
              <div key={index} className="text-center">
                <div className="mb-3 flex justify-center text-yellow-400">
                  {result.icon}
                </div>
                <div className="text-3xl font-bold text-yellow-400 mb-2">{result.metric}</div>
                <div className="text-lg">{result.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PPC ROI Showcase */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1 relative">
              <div className="bg-gradient-to-br from-orange-500 to-pink-600 rounded-2xl p-8 shadow-2xl">
                <Image 
                  src={imageConfig.platforms.analyticsPanel} 
                  alt="PPC Campaign Dashboard" 
                  className="rounded-lg shadow-lg"
                />
              </div>
              <div className="absolute -top-6 -right-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-600">320%</div>
                  <div className="text-sm text-gray-600">Average ROAS</div>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-4xl font-bold text-gray-800 mb-6">Maximize ROI with Strategic PPC</h2>
              <p className="text-lg text-gray-600 mb-8">
                Our data-driven PPC campaigns deliver immediate results. We optimize every dollar 
                of your ad spend to maximize conversions and grow your business profitably.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="bg-orange-100 p-3 rounded-lg">
                    <FiTarget className="text-orange-600 text-2xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 mb-2">Laser-Targeted Campaigns</h3>
                    <p className="text-gray-600">Reach your ideal customers at the perfect moment with precision targeting</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-orange-100 p-3 rounded-lg">
                    <FiDollarSign className="text-orange-600 text-2xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 mb-2">Budget Optimization</h3>
                    <p className="text-gray-600">Every rupee is optimized for maximum return on investment</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-orange-100 p-3 rounded-lg">
                    <FiBarChart className="text-orange-600 text-2xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 mb-2">Real-Time Optimization</h3>
                    <p className="text-gray-600">Continuous monitoring and adjustment for peak performance</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PPC Services */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Our PPC Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expert paid advertising management across all major platforms
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {ppcServices.map((service, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-all duration-300 group">
                <div className="text-orange-600 mb-6 group-hover:scale-110 transition-transform">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                
                <ul className="space-y-3 mb-6">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-3 text-gray-700">
                      <FiCheckCircle className="text-green-500 text-lg flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="border-t pt-6">
                  <div className="mb-4">
                    <div className="text-2xl font-bold text-orange-600">{service.price}</div>
                    <div className="text-sm text-gray-600">{service.duration}</div>
                    <div className="text-sm text-green-600 font-semibold mt-1">{service.roi}</div>
                  </div>
                  <Link
                    to="/contact"
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white py-3 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 group"
                  >
                    Get Started
                    <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PPC Packages */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">PPC Management Packages</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the perfect PPC package based on your advertising goals and budget
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {ppcPackages.map((pkg, index) => (
              <div 
                key={index} 
                className={`bg-white rounded-2xl shadow-lg p-8 relative ${
                  pkg.popular ? 'border-2 border-orange-500 transform scale-105' : 'border border-gray-200'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-orange-600 text-white px-6 py-2 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">{pkg.name}</h3>
                  <div className="text-4xl font-bold text-orange-600 mb-1">
                    {pkg.price}
                    <span className="text-lg text-gray-600 font-normal">{pkg.period}</span>
                  </div>
                  {pkg.adSpend && <div className="text-sm text-gray-600">{pkg.adSpend}</div>}
                  <p className="text-gray-600 mt-2">{pkg.description}</p>
                </div>

                <ul className="space-y-4 mb-8">
                  {pkg.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-3">
                      <FiCheckCircle className="text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  className={`block w-full text-center py-4 rounded-lg font-semibold transition-all duration-300 ${
                    pkg.popular 
                      ? 'bg-orange-600 hover:bg-orange-700 text-white' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                  }`}
                >
                  {pkg.price === 'Custom' ? 'Contact Sales' : 'Get Started'}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Why Choose Mirani Marketing for PPC?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We maximize every rupee of your ad spend with data-driven strategies
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiDollarSign className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Maximum ROI</h3>
              <p className="text-gray-600 leading-relaxed">
                Average 320% ROAS across all campaigns with continuous optimization
              </p>
            </div>
            <div className="text-center group">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiTarget className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Precision Targeting</h3>
              <p className="text-gray-600 leading-relaxed">
                Reach your ideal customers at the perfect moment with advanced targeting
              </p>
            </div>
            <div className="text-center group">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiBarChart className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Real-Time Optimization</h3>
              <p className="text-gray-600 leading-relaxed">
                Daily monitoring and adjustments to ensure peak performance
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PPC FAQs */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">PPC Advertising FAQs</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Common questions about our PPC management services
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                question: "How quickly can I see results from PPC?",
                answer: "Unlike SEO, PPC delivers immediate results. Your ads can start showing within hours of campaign launch. However, we recommend 2-3 months for optimal performance as we test and optimize campaigns for best results."
              },
              {
                question: "What's the minimum ad spend required?",
                answer: "We recommend a minimum monthly ad spend of ₹30,000-₹50,000 to see meaningful results. This allows us to test different audiences, ad creatives, and optimize for conversions effectively."
              },
              {
                question: "How do you determine my PPC strategy?",
                answer: "We analyze your business goals, target audience, competitors, and budget to create a custom PPC strategy. This includes platform selection (Google, Facebook, LinkedIn, etc.), audience targeting, ad creative strategy, and budget allocation."
              },
              {
                question: "What's the difference between PPC management fee and ad spend?",
                answer: "The management fee covers our expertise, strategy, campaign setup, ongoing optimization, reporting, and account management. Ad spend is the budget paid directly to platforms (Google, Facebook, etc.) for showing your ads."
              },
              {
                question: "How do you measure PPC success?",
                answer: "We track key metrics including ROAS (Return on Ad Spend), Cost Per Acquisition (CPA), Click-Through Rate (CTR), Conversion Rate, and most importantly - revenue generated. You'll receive detailed monthly reports showing all performance metrics."
              },
              {
                question: "Can you work with my existing campaigns?",
                answer: "Absolutely! We can audit your existing campaigns, identify optimization opportunities, and take over management. Most clients see immediate improvements within the first month of our management."
              },
              {
                question: "What platforms do you advertise on?",
                answer: "We manage campaigns across all major platforms: Google Ads (Search, Display, Shopping, YouTube), Facebook & Instagram Ads, LinkedIn Ads, Microsoft Ads, Amazon Advertising, and other platforms based on your audience."
              },
              {
                question: "Do I own the ad accounts and data?",
                answer: "Yes! You own all ad accounts, and we manage them on your behalf. All campaign data, audiences, and assets belong to you. If we part ways, you retain complete ownership of everything."
              }
            ].map((faq, index) => (
              <details key={index} className="bg-white rounded-xl shadow-lg p-6 group hover:shadow-2xl transition-all duration-300">
                <summary className="text-xl font-bold text-gray-800 cursor-pointer flex items-center justify-between group-hover:text-orange-600 transition-colors">
                  {faq.question}
                  <FiArrowRight className="transform group-open:rotate-90 transition-transform text-orange-600" />
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-pink-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-5xl font-bold mb-8">Ready to Accelerate Your Growth?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto opacity-90">
            Get a free PPC audit and discover how our data-driven strategies 
            can maximize your advertising ROI and drive qualified leads.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-yellow-400 text-gray-800 px-10 py-5 rounded-lg font-bold text-lg hover:bg-yellow-300 transition-all duration-300 shadow-xl"
            >
              Get Free PPC Audit
            </Link>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-10 py-5 rounded-lg font-bold text-lg hover:bg-white hover:text-orange-600 transition-all duration-300"
            >
              Schedule Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};